import { useMemo } from "react";
import { BarChart2, Info } from "lucide-react";
import { MathText } from "./MathText";

export interface ContentFigure {
  id: string;
  caption?: string;
  description?: string;
  page?: number | null;
}

type GeneratedContentProps = {
  children?: string | null;
  className?: string;
  /** Legacy prop kept for compatibility; MathText handles math delimiters directly. */
  normalizeMath?: boolean;
  inline?: boolean;
  figures?: ContentFigure[];
};

export function GeneratedContent({
  children,
  className = "",
  inline = false,
  figures = [],
}: GeneratedContentProps) {
  const source = children == null ? "" : String(children);
  if (!source) return null;

  // Build a lookup map of figures by ID
  const figureMap = useMemo(() => {
    const map = new Map<string, ContentFigure>();
    for (const f of figures) {
      if (f.id) map.set(f.id.toLowerCase(), f);
    }
    return map;
  }, [figures]);

  // Regex to detect [FIGURE fig_id] or [FIGURE fig_id: Caption (Page X)]
  const figureRegex = useMemo(
    () => /\[FIGURE\s+([a-zA-Z0-9_\-]+)(?::\s*([^\]]+))?\]/gi,
    [],
  );

  // If text has no figure placeholders or inline mode, render MathText directly
  const hasFigurePlaceholders = /\[FIGURE\s+[a-zA-Z0-9_\-]+/i.test(source);

  if (inline || !hasFigurePlaceholders) {
    return <MathText inline={inline} className={className}>{source}</MathText>;
  }

  // Parse source into segments alternating between markdown text and figure cards
  const segments: Array<{ type: "markdown" | "figure"; content: string; figId?: string; inlineCaption?: string }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = figureRegex.exec(source)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "markdown",
        content: source.substring(lastIndex, match.index),
      });
    }

    segments.push({
      type: "figure",
      content: match[0],
      figId: match[1],
      inlineCaption: match[2],
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < source.length) {
    segments.push({
      type: "markdown",
      content: source.substring(lastIndex),
    });
  }

  return (
    <div className={`generated-content prose prose-sm max-w-none ${className}`.trim()}>
      {segments.map((seg, idx) => {
        if (seg.type === "markdown") {
          if (!seg.content.trim()) return null;
          return (
            <MathText key={idx}>
              {seg.content}
            </MathText>
          );
        }

        const figId = seg.figId || "";
        const figure = figureMap.get(figId.toLowerCase());
        const caption = figure?.caption || seg.inlineCaption || `Figure ${figId.replace(/^fig_?/i, "")}`;
        const description = figure?.description || "";
        const page = figure?.page;

        return (
          <div
            key={idx}
            className="figure-card not-prose my-4 rounded-sm border border-border bg-card p-3.5 shadow-sm surface-sm"
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 rounded-xs bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <BarChart2 size={13} />
                </div>
                <h4 className="font-bold text-xs sm:text-sm text-foreground truncate">
                  {caption}
                </h4>
              </div>
              {page != null && (
                <span className="text-xs px-2 py-0.5 rounded-xs bg-secondary text-muted-foreground font-mono font-semibold shrink-0 border border-border">
                  Page {page}
                </span>
              )}
            </div>

            {description ? (
              <div className="bg-secondary/60 rounded-xs p-3 border border-border text-xs text-foreground space-y-1">
                <div className="flex items-center gap-1 font-semibold text-xs uppercase tracking-wider text-primary">
                  <Info size={11} /> Visual Diagram Description
                </div>
                <p className="leading-relaxed font-benton text-muted-foreground text-xs">
                  {description}
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Diagram placeholder ({figId}) — text description preserved.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

